import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HandHeart, Home as HomeIcon, Car, Calendar, Heart } from "lucide-react";
import { ZEFFY_MAIN } from "@/constants/config";
import PageMeta from "@/components/PageMeta";

export default function GetInvolved() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    full_name: user?.name || "", email: user?.email || "", phone: "", role: "volunteer",
    availability: "weekends", experience: "", location: "", message: "",
  });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/volunteer-applications", form);
      setSent(true);
    } catch {
      alert("Something went wrong. Please try again.");
    }
  };

  return (
    <div>
      <PageMeta
        title="Foster, Volunteer & Get Involved | Lavish 808 Bulliez Rescued Paws"
        description="Foster a bulldog, volunteer at events, help with transport from Hawaiʻi, or join our ʻohana. Serving Murrieta, Riverside County and Southern California rescue efforts."
      />
      <section className="pt-20 pb-12" style={{ backgroundColor: "#F7ECD9" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "#E27E4E" }}>Kōkua • Get Involved</div>
          <h1 className="font-serif text-5xl md:text-7xl mb-6" style={{ color: "#0E5C6E" }}>Aloha starts with you.</h1>
          <p className="text-lg max-w-2xl leading-relaxed" style={{ color: "#2D2B2A" }}>
            Volunteers, fosters, and transporters are the backbone of every rescue. Join our ʻohana — here's how you can kōkua.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-6">
          <RoleCard icon={<HomeIcon />} title="Foster" desc="Open your home to a bulldog in transition. We cover vet & food." />
          <RoleCard icon={<HandHeart />} title="Volunteer" desc="Help with events, admin, socials, dog walks, and adoption days." />
          <RoleCard icon={<Car />} title="Transport" desc="Help move pups from Hawaii airports or between fosters." />
          <RoleCard icon={<Calendar />} title="Events" desc="Support fundraisers, pop-ups, and adoption fairs." />
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 md:p-14 rounded-3xl" style={{ backgroundColor: "#F7ECD9" }}>
            <h2 className="font-serif text-4xl mb-2" style={{ color: "#0E5C6E" }}>Sign up to help</h2>
            <p className="mb-8" style={{ color: "#2D2B2A" }}>Tell us how you'd like to be part of the ʻohana. We'll follow up within a week.</p>

            {sent ? (
              <div className="p-8 rounded-2xl text-center bg-white" data-testid="volunteer-success">
                <Heart size={32} color="#0E5C6E" fill="#0E5C6E" className="mx-auto mb-3" />
                <div className="font-serif text-3xl mb-2" style={{ color: "#0E5C6E" }}>Mahalo nui loa!</div>
                <div style={{ color: "#2D2B2A" }}>We'll be in touch soon with next steps.</div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="volunteer-form" aria-label="Volunteer and foster application">
                <div className="grid md:grid-cols-2 gap-4">
                  <input required placeholder="Full name" aria-label="Full name" className="form-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="volunteer-name" />
                  <input required type="email" placeholder="Email" aria-label="Email address" className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="volunteer-email" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <input required placeholder="Phone" aria-label="Phone number" className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="volunteer-phone" />
                  <input required placeholder="City, State" aria-label="City and state" className="form-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="volunteer-location" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <select aria-label="How you'd like to help" className="form-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="volunteer-role">
                    <option value="foster">Foster</option>
                    <option value="volunteer">General volunteer</option>
                    <option value="transport">Transport</option>
                    <option value="events">Events</option>
                  </select>
                  <select aria-label="Your availability" className="form-input" value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} data-testid="volunteer-availability">
                    <option value="weekends">Weekends</option>
                    <option value="weekdays">Weekdays</option>
                    <option value="both">Both</option>
                    <option value="occasional">Occasional</option>
                  </select>
                </div>
                <textarea rows="3" placeholder="Any experience with dogs? Special skills? Tell us about yourself." aria-label="Experience and skills" className="form-input" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} data-testid="volunteer-experience" />
                <textarea rows="3" placeholder="Anything else you'd like us to know?" aria-label="Additional notes" className="form-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="volunteer-message" />
                <button type="submit" className="btn-primary" data-testid="volunteer-submit">Send my application</button>
              </form>
            )}
          </div>

          <div className="mt-12 text-center">
            <p className="mb-4" style={{ color: "#2D2B2A" }}>Can't volunteer right now? You can still make a difference.</p>
            <a href={ZEFFY_MAIN} target="_blank" rel="noopener noreferrer" className="btn-donate" data-testid="involved-donate">
              <Heart size={18} fill="white" /> Donate via Zeffy
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function RoleCard({ icon, title, desc }) {
  return (
    <div className="p-8 rounded-3xl bg-white border" style={{ borderColor: "#EADCC5" }}>
      <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: "#E27E4E", color: "white" }}>
        {icon}
      </div>
      <h3 className="font-serif text-xl mb-2" style={{ color: "#0E5C6E" }}>{title}</h3>
      <p className="text-sm" style={{ color: "#2D2B2A" }}>{desc}</p>
    </div>
  );
}
